// Workspace-local design constants for the Unified Workspace (Direction A) redesign.
//
// Color tokens that already exist in the vendor theme are referenced via
// `tokens` (app/components/MatxTheme/themeColors.js). The status amber/green
// and Procore orange palettes are NOT in the vendor theme and the theme file
// is off-limits, so they live here as the single source of truth. If they are
// ever added to the theme, migrate these references and delete the literals.

import { tokens } from 'app/components/MatxTheme/themeColors';

// --- Font stacks (handoff: Sora headings / Inter body / Plex Mono UI) ------

export const FONT_SORA = "'Sora', sans-serif";
export const FONT_INTER = "'Inter', sans-serif";
export const FONT_MONO = "'IBM Plex Mono', monospace";

// External AR app — the only real "create observation" path today.
//
// This is a Universal Link, not a direct App Store URL. On iOS with the
// Argyle Build app installed, tapping this opens the app directly. Otherwise
// the browser loads the URL, and the host is expected to 302 to the App Store.
//
// Requires (coordinate with the iOS team / hosting):
//   1. iOS app ships with `applinks:argyle.build` in its Associated Domains
//      entitlement.
//   2. https://argyle.build/.well-known/apple-app-site-association serves a
//      valid AASA file listing the app's bundle ID + team ID and the `/app/*`
//      path pattern.
//   3. https://argyle.build/app (non-app traffic) 302s iOS user agents to
//      the App Store URL below and serves a sensible page elsewhere.
//
// Until (1) and (2) ship, this URL will simply load argyle.build/app in the
// browser — so make sure (3) is in place before flipping this.
export const AR_APP_URL = 'https://argyle.build/download';

// Direct App Store URL — kept for the hosting-side fallback redirect and any
// future "Get the app" CTA that should always go straight to the store.
export const AR_APP_STORE_URL =
    'https://apps.apple.com/us/app/argyle-build/id1608445714';

// --- Missing palettes (not in vendor theme) -------------------------------

export const STATUS_AMBER = {
    bg: '#FFF6E0',
    fg: '#7A5300',
    border: '#FAE5A0',
};

export const STATUS_GREEN = {
    bg: '#E6F5EA',
    fg: '#1F6B36',
    border: '#BFE3CB',
};

export const PROCORE_ORANGE = {
    fg: '#F47820',
    dark: '#D9641A',
    bg: '#FFF1E5',
    border: '#FFD9BC',
};

// --- Status chip color map -------------------------------------------------
// status string (case-insensitive) -> { bg, fg, border }

export const STATUS_COLORS = {
    open: {
        bg: tokens.pinkAccent[50],
        fg: tokens.pinkAccent[700],
        border: tokens.pinkAccent[100],
    },
    'in review': STATUS_AMBER,
    pending: STATUS_AMBER,
    resolved: STATUS_GREEN,
    active: STATUS_GREEN,
    draft: {
        bg: tokens.grey[100],
        fg: tokens.grey[700],
        border: tokens.grey[200],
    },
};

export const DEFAULT_STATUS_COLOR = {
    bg: tokens.grey[100],
    fg: tokens.grey[700],
    border: tokens.grey[200],
};

export const getStatusColor = (status) =>
    STATUS_COLORS[String(status ?? '').toLowerCase()] ?? DEFAULT_STATUS_COLOR;

export const PROCORE_COLORS = PROCORE_ORANGE;

// Autodesk Construction Cloud (post-2021 Autodesk brand is black/near-black).
export const ACC_COLORS = {
    fg: '#1A1A1A',
    dark: '#000000',
    bg: '#F2F2F2',
    border: '#D9D9D9',
};

// --- Tab definitions -------------------------------------------------------
// Icons use the @mui/icons-material outlined set per the handoff icon map.
// Order is fixed by the design: Observations, Users, AR Media, QR, Models. Files was added
// after the redesign for the project file tree.
//
// The live stream is no longer a tab — it lives in the always-on right rail
// (LiveStreamRailCard); joining a call takes over the whole view until the
// user leaves.

export const TAB_DEFS = [
    { key: 'observations', label: 'Observations', icon: 'VisibilityOutlined' },
    { key: 'users', label: 'Users', icon: 'PeopleOutlined' },
    // Key stays 'photos' so existing ?tab=photos links keep working.
    { key: 'photos', label: 'AR Media', icon: 'PermMediaOutlined' },
    { key: 'qr', label: 'QR Codes', icon: 'QrCodeOutlined' },
    { key: 'model', label: 'Models', icon: 'ViewInArOutlined' },
    // Project file tree (file-service v0.4): documents only, all roles.
    { key: 'files', label: 'Files', icon: 'FolderOutlined' },
];

export const TAB_KEYS = TAB_DEFS.map((t) => t.key);

export const DEFAULT_TAB = 'observations';

export const isValidTab = (key) => TAB_KEYS.includes(key);
